import { Dispatch, MutableRefObject, SetStateAction, useMemo } from 'react';

import { Grid, Box, Typography, FormControlLabel, Switch, Button } from '@mui/material';
import { Save } from '@mui/icons-material';
import { useMatch } from 'react-router-dom';

import { Card, RouterLink } from 'components';
import { CompetitorsAddTable } from 'components/CompetitorsAddTable/CompetitorsAddTable';
import strings, { url } from 'constants/index';
import { EnumValueNames, useEnum } from 'contexts/EnumValuesContext';
import { CompetitorTeamData } from 'features/competitors';
import { urlByCompetitorTypeMappingRouting } from 'features/competitors/Competitors.utils';
import { CompetitorDouble } from 'features/competitors/doubles/Doubles.types';
import { PersonData } from 'features/persons';
import {
  ActionsColumn,
  IdColumn,
  NameColumn,
  getGenderTypeColumn,
  LocalityNameColumn,
  PlayerIdColumn,
  getIdFormatter,
} from 'utils/table.utils';

import { competitorsFormFieldStyles } from './CompetitorsFormField.styles';
import { Table, TableColumn } from 'components/Table';

interface Props {
  competitors: Competitor[];
  rows: EditableCompetitor[];
  idsRef: MutableRefObject<Record<number, HTMLDivElement>>;
  setFilterBy: Dispatch<SetStateAction<PaginationFilter[]>>;
  setSortBy: Dispatch<SetStateAction<PaginationSort[]>>;
  genderType?: string;
  sportId?: number;
  seasonId?: number;
  required?: boolean;
  customColumns?: Array<TableColumn>;
  customFilters?: Array<PaginationFilter>;
  onEditCell?: (columnId: string | number, value: any, row: Record<string, any>) => Promise<void>;
  onAdd?: (competitor: CompetitorTeamData | PersonData | CompetitorDouble) => void;
  isActiveContracts?: boolean;
  setActiveContracts?: Dispatch<SetStateAction<boolean>>;
  onSaveAll?: () => void | Promise<void>;
  hasModifiedCompetitors?: boolean;
}

export const BaseCompetitorsFormField = ({
  sportId,
  customColumns,
  customFilters,
  onEditCell,
  competitors,
  onAdd,
  rows,
  idsRef,
  setFilterBy,
  setSortBy,
  seasonId,
  isActiveContracts,
  setActiveContracts,
  genderType,
  onSaveAll,
  hasModifiedCompetitors,
}: Props) => {
  const isSquads = !!useMatch(`${url.base_teams}/:teamId`);
  const staticFilters = useMemo<Array<PaginationFilter>>(
    () => [
      ...(competitors.length > 0 && competitors.length < 200
        ? [
            {
              columnId: 'id',
              condition: 'nin',
              value: competitors.map((item: EditableCompetitor) => item.id).join(','),
            },
            ...(genderType !== undefined && genderType !== 'Mixed'
              ? [
                  {
                    columnId: 'genderType',
                    condition: 'equals',
                    value: genderType,
                  },
                ]
              : []),
          ]
        : []),
      ...(sportId ? [{ columnId: 'sport.id', value: sportId }] : []),
      ...(customFilters ? customFilters : []),
    ],
    [competitors, customFilters, genderType, sportId],
  );

  const gendertype = useEnum(EnumValueNames.Gender);

  const getCompetitorUrl = useMemo(
    () => competitor => {
      const pathName = competitor.competitorType
        ? urlByCompetitorTypeMappingRouting[competitor.competitorType]
        : urlByCompetitorTypeMappingRouting.Person;
      return (
        <RouterLink data-testid="show-details" to={`${pathName}/${competitor.id}`}>
          {competitor.id}
        </RouterLink>
      );
    },
    [],
  );
  const columns = useMemo(() => {
    const columns = [
      {
        ...IdColumn,
        formatter: (value: number, competitor) => {
          if (!competitor) {
            return value;
          }

          return (
            <div ref={el => (idsRef.current[competitor.id] = el)}>
              {getCompetitorUrl(competitor)}
            </div>
          );
        },
      },
      {
        ...PlayerIdColumn,
        formatter: getIdFormatter(url.base_players),
      },
      {
        ...NameColumn,
        label: 'Competitors',
      },
      LocalityNameColumn,
      getGenderTypeColumn(gendertype),
      ActionsColumn,
    ];

    if (customColumns) {
      columns.splice(5, 0, ...customColumns);
    }

    return columns;
  }, [customColumns, gendertype, getCompetitorUrl, idsRef]);
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card title={strings.competitors_add} contentClasses="nonPaddedContent" expandable>
          <CompetitorsAddTable
            genderType={genderType}
            seasonId={seasonId}
            staticFilters={staticFilters}
            onAdd={onAdd}
            competitors={competitors}
          />
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card
          muiHeaderProps={
            isSquads
              ? {
                  action: (
                    <Box display="flex" alignItems="center">
                      {onSaveAll && (
                        <Button
                          data-testid="competitors-save-all"
                          aria-label="competitors-save-all"
                          variant="contained"
                          size="small"
                          sx={{ marginLeft: '5px' }}
                          startIcon={<Save />}
                          disabled={!hasModifiedCompetitors}
                          onClick={onSaveAll}
                        >
                          {strings.save}
                        </Button>
                      )}
                      <FormControlLabel
                        sx={competitorsFormFieldStyles.contractsSwitch}
                        control={
                          <Switch
                            data-testid="team-contracts-switch"
                            aria-label="team-contracts-switch"
                            checked={!isActiveContracts}
                            onChange={() => setActiveContracts(!isActiveContracts)}
                          />
                        }
                        label={strings.inactive_contracts}
                      />
                    </Box>
                  ),
                }
              : null
          }
          title={
            <Box display="flex" justifyContent="space-between">
              <div>{strings.competitors_list}</div>
              <Typography>
                {strings.total_count}: {competitors.length}
              </Typography>
            </Box>
          }
          data-testid="competitors-list"
          contentClasses="nonPaddedContent"
        >
          <Box sx={competitorsFormFieldStyles.listContainer}>
            <Table
              localStorageKey="competitorsFormField"
              columns={columns}
              loading={false}
              onFilterBy={setFilterBy}
              onRequestSort={setSortBy}
              onSearch={() => false}
              rows={rows}
              onEditCell={onEditCell}
              footerHidden
            />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};
